// Thin client for the API. Every shape comes from packages/contracts.
import type * as T from "./types.ts";

export class ApiError extends Error {
  readonly problem: T.Problem;
  constructor(problem: T.Problem) { super(problem.title); this.problem = problem; }
}

let csrfToken = "";
export const setCsrf = (token: string) => { csrfToken = token; };

async function request<R>(method: string, path: string, body?: unknown): Promise<R> {
  const headers: Record<string, string> = {};
  if (method !== "GET" && csrfToken) headers["x-csrf-token"] = csrfToken;
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) { payload = JSON.stringify(body); headers["content-type"] = "application/json"; }
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, { method, headers, body: payload, credentials: "same-origin" });
  } catch {
    throw new ApiError({ type: "about:blank", title: "Sin conexión", status: 0, detail: "No se ha podido contactar con el servidor. Comprueba la conexión." });
  }
  if (res.status === 204) return undefined as R;
  const data = res.headers.get("content-type")?.includes("json") ? await res.json() : null;
  if (!res.ok) throw new ApiError(data ?? { type: "about:blank", title: "Error del servidor", status: res.status });
  return data as R;
}

export const api = {
  health: () => request<T.Health>("GET", "/health"),
  loginOptions: () => request<T.LoginOptions>("GET", "/auth/options"),
  demoLogin: (userId: string, accessCode?: string) => request<T.Me>("POST", "/auth/demo", { userId, ...(accessCode ? { accessCode } : {}) }),
  logout: () => request<void>("POST", "/auth/logout"),
  me: () => request<T.Me>("GET", "/me"),
  conversations: () => request<T.Page<T.Conversation>>("GET", "/conversations"),
  createConversation: () => request<T.Conversation>("POST", "/conversations", {}),
  messages: (id: string) => request<T.MessagePage>("GET", `/conversations/${id}/messages`),
  send: (id: string, text: string, attachments: string[] = []) => request<{ messageId: string; taskId: string }>("POST", `/conversations/${id}/messages`, { text, ...(attachments.length ? { attachments } : {}) }),
  upload: (id: string, file: File) => { const f = new FormData(); f.append("file", file, file.name); return request<T.Attachment>("POST", `/conversations/${id}/attachments`, f); },
  sendVoice: (id: string, audio: Blob, durationMs: number) => {
    const form = new FormData();
    form.append("durationMs", String(Math.round(durationMs)));
    form.append("audio", audio, "nota");
    return request<{ messageId: string; taskId: string }>("POST", `/conversations/${id}/voice`, form);
  },
  confirmations: (status?: T.Confirmation["status"]) => request<T.Page<T.Confirmation>>("GET", `/confirmations${status ? `?status=${status}` : ""}`),
  decide: (id: string, decision: "approve" | "reject") => request<T.Confirmation>("POST", `/confirmations/${id}/decision`, { decision }),
  tasks: () => request<T.Page<T.Task>>("GET", "/tasks"),
  schedules: () => request<T.Page<T.Schedule>>("GET", "/schedules"),
  cancelSchedule: (id: string) => request<void>("DELETE", `/schedules/${id}`),
  audit: () => request<T.Page<T.AuditEvent>>("GET", "/audit?limit=100"),
  preferences: () => request<T.Preferences>("GET", "/preferences"),
  updatePreferences: (p: Partial<T.Preferences>) => request<T.Preferences>("PATCH", "/preferences", p),
  memories: () => request<T.Page<T.Memory>>("GET", "/memories"),
  forget: (id: string) => request<void>("DELETE", `/memories/${id}`),
  memoryNotes: () => request<T.Page<T.MemoryNote>>("GET", "/memories/notes"),
  keepNote: (id: string) => request<T.Memory>("POST", `/memories/notes/${id}/keep`),
  dropNote: (id: string) => request<void>("DELETE", `/memories/notes/${id}`),
  consolidate: () => request<T.MemoryRun>("POST", "/memories/consolidate"),
  memoryRuns: () => request<T.Page<T.MemoryRun>>("GET", "/memories/runs"),
  pushKey: () => request<{ publicKey: string }>("GET", "/push/key"),
  pushSubscribe: (sub: PushSubscriptionJSON) => request<void>("POST", "/push/subscriptions", sub),
  pushUnsubscribe: (endpoint: string) => request<void>("DELETE", "/push/subscriptions", { endpoint }),
  pushTest: () => request<{ delivered: number }>("POST", "/push/test"),
  panic: () => request<T.PanicState>("POST", "/panic"),
  resetDemo: () => request<void>("POST", "/demo/reset"),
  simulateChat: (scenario: "comida" | "conflicto" | "felicitacion") => request<{ messageId: string }>("POST", "/demo/whatsapp", { scenario }),
  admin: {
    users: () => request<T.Page<T.UserSummary>>("GET", "/admin/users"),
    user: (id: string) => request<T.UserDetail>("GET", `/admin/users/${id}`),
    create: (u: T.CreateUser) => request<T.UserDetail>("POST", "/admin/users", u),
    update: (id: string, u: T.UpdateUser) => request<T.UserDetail>("PATCH", `/admin/users/${id}`, u),
    rules: (id: string, rules: T.Rule[]) => request<T.UserDetail>("PUT", `/admin/users/${id}/rules`, rules),
    limits: (id: string, perOperationCents: number, dailyCents: number) => request<T.UserDetail>("PUT", `/admin/users/${id}/limits`, { perOperationCents, dailyCents }),
    soul: () => request<T.Soul>("GET", "/admin/soul"),
    setSoul: (text: string) => request<T.Soul>("PUT", "/admin/soul", { text }),
    panic: (id: string, active: boolean) => request<T.UserDetail>("PUT", `/admin/users/${id}/panic`, { active }),
    services: (id: string) => request<T.ServiceAccount[]>("GET", `/admin/users/${id}/services`),
    addService: (id: string, a: T.AddServiceAccount) => request<T.ServiceAccount>("POST", `/admin/users/${id}/services`, a),
    deleteService: (id: string, serviceId: string) => request<void>("DELETE", `/admin/users/${id}/services/${serviceId}`),
    audit: () => request<T.Page<T.AuditEvent>>("GET", "/admin/audit?limit=100"),
    verify: () => request<T.AuditVerification>("GET", "/admin/audit/verification"),
  },
};
